import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { ShieldCheck, CreditCard, Smartphone, CheckCircle, Lock } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;

export default function Checkout() {
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!token) {
      toast.error('कृपया पहले लॉगिन करें');
      navigate('/login');
    }
  }, [token, navigate]);

  const courseDetails = {
    name: 'Digital Research Mastery Course',
    price: 10999,
    duration: '15 Days',
    features: [
      'Digital Research Skills',
      'AI Tools & Techniques',
      'Online Career Growth',
      'Certificate of Completion',
      'Lifetime Access',
      'Live Sessions'
    ]
  };

  const handlePayment = async () => {
    if (!API) {
      toast.error('Backend is not configured yet');
      return;
    }
    if (paymentProcessing) {
      toast.warning('Payment already in progress...');
      return;
    }

    setLoading(true);
    setPaymentProcessing(true);

    try {
      // Create order
      const response = await axios.post(
        `${API}/payments/create-order`,
        {
          amount: courseDetails.price,
          currency: 'INR',
          student_id: user.id,
          course_id: 'digital_research_mastery_2026'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { order_id, amount, currency, key_id, mock } = response.data;

      // Check if Razorpay is available
      if (typeof window.Razorpay === 'undefined') {
        toast.error('Payment gateway not loaded. Please refresh the page.');
        setLoading(false);
        setPaymentProcessing(false);
        return;
      }

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        order_id: order_id,
        name: 'MKDRP',
        description: courseDetails.name,
        image: 'https://customer-assets.emergentagent.com/job_digital-research-hub/artifacts/902v0dmw_logo.png',
        handler: async function (paymentResponse) {
          try {
            // Verify payment
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
              student_id: user.id
            });

            // Success
            setPaymentProcessing(false);
            toast.success('🎉 Payment Successful – Welcome to MKDRP Course!', {
              duration: 5000
            });
            
            setTimeout(() => {
              navigate('/courses');
            }, 2000);
          } catch (error) {
            setPaymentProcessing(false);
            toast.error('Payment verification failed. Please contact support.');
            console.error('Verification error:', error);
          }
        },
        prefill: {
          name: user.full_name,
          email: user.email,
          contact: user.phone || ''
        },
        theme: {
          color: '#FF9933'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            setPaymentProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      // Open Razorpay
      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', function (response) {
        setPaymentProcessing(false);
        toast.error('❌ Payment Failed: ' + response.error.description);
        console.error('Payment failed:', response.error);
      });

      rzp.open();
      setLoading(false);

    } catch (error) {
      setLoading(false);
      setPaymentProcessing(false);
      console.error('Payment error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } else {
        toast.error('Failed to initiate payment. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50">
      <Header />
      <WhatsAppButton />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-extrabold font-heading gradient-text mb-4" data-testid="checkout-title">
            Secure Checkout
          </h1>
          <p className="text-gray-600 font-hindi">सुरक्षित भुगतान प्रक्रिया</p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Course Details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Details</h2>
            
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-xl mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{courseDetails.name}</h3>
              <p className="text-gray-600 mb-4">Duration: {courseDetails.duration}</p>
              
              <div className="grid sm:grid-cols-2 gap-3">
                {courseDetails.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Available Payment Methods</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-lg">
                  <Smartphone className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium">UPI</span>
                </div>
                <div className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">Cards</span>
                </div>
                <div className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-lg">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">Net Banking</span>
                </div>
              </div>
            </div>

            {/* Security Features */}
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Lock className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-green-900 mb-1">100% Secure Payment</h4>
                  <p className="text-sm text-green-700">Your payment information is encrypted and secure. We never store your card details.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Payment Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Payment Summary</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-700">
                  <span>Course Fee</span>
                  <span className="font-semibold">₹{courseDetails.price}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span className="font-semibold">₹0</span>
                </div>
                <div className="border-t-2 border-gray-200 pt-4 flex justify-between">
                  <span className="text-lg font-bold text-gray-900">Total Amount</span>
                  <span className="text-2xl font-extrabold text-orange-600">₹{courseDetails.price}</span>
                </div>
              </div>

              <button
                onClick={handlePayment}
                disabled={loading || paymentProcessing}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="pay-now-button"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : paymentProcessing ? (
                  'Payment in Progress...'
                ) : (
                  <>
                    <ShieldCheck className="w-5 h-5" />
                    Pay ₹{courseDetails.price}
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                By proceeding, you agree to our Terms & Conditions
              </p>
            </div>
          </motion.div>
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <p className="text-sm text-gray-600 mb-4">Trusted by 10,000+ Students</p>
          <div className="flex justify-center items-center gap-6 flex-wrap">
            <img src="https://razorpay.com/assets/razorpay-glyph.svg" alt="Razorpay" className="h-8 opacity-50" />
            <span className="text-gray-400 font-semibold">SSL Secured</span>
            <span className="text-gray-400 font-semibold">256-bit Encryption</span>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}
